import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { getRandomId, WcagService } from '@lib/core';
import { TranslateModule } from '@ngx-translate/core';
import { DigitDirective } from 'libs/core/src/lib/directive/digit-only.directive';

@Component({
  selector: 'app-numeric-stepper',
  templateUrl: './numeric-stepper.component.html',
  standalone: true,
  imports: [CommonModule, DigitDirective, TranslateModule]
})
export class NumericStepperComponent {
  @Output() quantityEvent = new EventEmitter<number>();
  @Output() quantityChanged = new EventEmitter<{ prev: number; current: number; diff: number }>();

  @Input() maxQuantity = 99;

  @Input() minQuantity = 1;
  @Input() id = 'defaultNumericStepperId';
  @Input() inputField = false;

  private _quantity: number = 1;
  @Input() get quantity() {
    return this._quantity;
  }
  set quantity(value: number) {
    this._quantity = value;
    this.check();
  }

  @Input() step = 1;
  @Input() disabled = false;
  @Input() selectOnFocus = false;

  @Input() decrementIcon = 'fa fa-minus px-3';
  @Input() incrementIcon = 'fa fa-plus px-3';

  @Input() quantityClass = 'p-0 px-sm-2';

  @Input() ariaLabel: string = '';

  inputId: string;

  canDecrement = false;
  canIncrement = true;
  prev: number;

  constructor(private wcagService: WcagService) {}

  private announceChange(): void {
    const message = `Ilość: ${this.quantity}`;
    this.wcagService.announce(message);
  }

  ngOnInit() {
    this.prev = this._quantity;
    this.inputId = this.id ? `numeric-stepper-${this.id}` : `numeric-stepper-${getRandomId()}`;
    this.check();
  }

  public onInput(event): void {
    const inputElement = event.target as HTMLInputElement;
    let currentValue = Number(inputElement.value);
    if (currentValue > this.maxQuantity) {
      inputElement.value = this.maxQuantity.toString();
      currentValue = this.maxQuantity;
    } else if (currentValue < this.minQuantity) {
      inputElement.value = this.minQuantity.toString();
      currentValue = this.minQuantity;
    }

    this.prev = this._quantity;
    this.quantity = currentValue;
    this.announceChange();
  }

  public onChange(event): void {
    const value = Number(event?.target?.value ?? 0);
    if (this.prev !== value && value >= this.minQuantity && value <= this.maxQuantity) {
      this.changeQuantity(value);
      this.check();
    }
  }

  public increment(): void {
    const newValue = this._quantity + this.step;
    if (newValue <= this.maxQuantity) {
      this.changeQuantity(newValue);
      this.check();
    }
  }

  public decrement(): void {
    const newValue = this._quantity - this.step;
    if (newValue >= this.minQuantity) {
      this.changeQuantity(newValue);
      this.check();
    }
  }

  public onKeyDown(event: KeyboardEvent): void {
    if (this.disabled) {
      return;
    }

    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault();
        this.increment();
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.decrement();
        break;
      default:
        break;
    }
  }

  selectInput(event: FocusEvent): void {
    if (!this.selectOnFocus) {
      return;
    }
    const inputElement = event.target as HTMLInputElement;
    inputElement.select();
  }

  private changeQuantity(newValue: number) {
    this.prev = this._quantity;

    this.quantity = newValue;
    this.quantityEvent.emit(newValue);
    this.quantityChanged.emit({
      prev: this.prev,
      current: this.quantity,
      diff: this.quantity - this.prev
    });

    this.announceChange();
  }

  private check() {
    this.canIncrement = this._quantity + this.step <= this.maxQuantity;
    this.canDecrement = this._quantity - this.step >= this.minQuantity;
  }
}
